import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

type PredictionRow = {
  match_id: string;
  pred_home: number;
  pred_away: number;
  points_awarded: number;
  is_exact: boolean;
  locked_at: string | null;
};

const PredictionInput = z.object({
  match_id: z.string().min(1).max(64),
  pred_home: z.number().int().min(0).max(20),
  pred_away: z.number().int().min(0).max(20),
});

export const submitPrediction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => PredictionInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("bracket_predictions")
      .upsert(
        {
          user_id: userId,
          match_id: data.match_id,
          pred_home: data.pred_home,
          pred_away: data.pred_away,
        },
        { onConflict: "user_id,match_id" },
      );
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  });

export const getMyBracket = createServerFn({ method: "GET" })
  .handler(async () => {
    // P1: public view — anonymous visitors see the bracket; predictions are
    // only loaded when the caller is authenticated (auth header attached by
    // attachSupabaseAuth). Login is only required on submit.
    const { data: matches } = await supabaseAdmin
      .from("bracket_matches")
      .select("id, home_team, away_team, home_flag, away_flag, kickoff_at, status, real_home_score, real_away_score, stage")
      .order("kickoff_at", { ascending: true })
      .limit(64);

    let predictions: PredictionRow[] = [];
    try {
      const { createClient } = await import("@supabase/supabase-js");
      const url = process.env.SUPABASE_URL!;
      const anon = process.env.SUPABASE_PUBLISHABLE_KEY!;
      const { getRequestHeader } = await import("@tanstack/react-start/server");
      const authHeader = getRequestHeader("authorization");
      if (authHeader) {
        const sb = createClient(url, anon, {
          global: { headers: { Authorization: authHeader } },
          auth: { persistSession: false, autoRefreshToken: false },
        });
        const { data: userRes } = await sb.auth.getUser();
        if (userRes.user) {
          const { data: preds } = await sb
            .from("bracket_predictions")
            .select("match_id, pred_home, pred_away, points_awarded, is_exact, locked_at")
            .eq("user_id", userRes.user.id);
          predictions = (preds ?? []) as PredictionRow[];
        }
      }
    } catch {
      /* anonymous — no predictions */
    }
    return { matches: matches ?? [], predictions };
  });

export const getBracketLeaderboard = createServerFn({ method: "GET" })
  .inputValidator((input: { limit?: number } | undefined) => ({
    limit: Math.min(Math.max(input?.limit ?? 50, 1), 200),
  }))
  .handler(async ({ data }) => {
    const { data: rows, error } = await supabaseAdmin
      .from("bracket_leaderboard")
      .select("user_id, total_points, exact_count, hit_count, played_count")
      .order("total_points", { ascending: false })
      .limit(data.limit);
    if (error) return { rows: [], error: error.message };
    return { rows: rows ?? [], error: null };
  });